import Big from 'big.js';
import {
  scientificNotationToString,
  toNonDivisibleNumber,
  toReadableNumber,
} from '../utils/numbers';
import { getStableTokenIndex } from './near';
import { StablePool } from './pool';
import _ from 'lodash';
import { getStablePoolDecimal } from 'src/utils/stablePoolFormatting';
const FEE_DIVISOR = 10000;

const tradeFee = (amount: number, trade_fee: number) => {
  return (amount * trade_fee) / FEE_DIVISOR;
};

const calc_d = (amp: number, c_amounts: number[]) => {
  const token_num = c_amounts.length;
  const sum_amounts = _.sum(c_amounts);
  let d_prev = 0;
  let invariant = sum_amounts;
  for (let iteration = 0; iteration < 256; iteration++) {
    let d_prod = invariant;
    for (let c_amount of c_amounts) {
      d_prod = (d_prod * invariant) / (c_amount * token_num);
    }
    d_prev = invariant;
    const ann = amp * token_num ** token_num;
    const numerator = d_prev * (d_prod * token_num + ann * sum_amounts);
    const denominator = d_prev * (ann - 1) + d_prod * (token_num + 1);
    invariant = numerator / denominator;
    if (Math.abs(invariant - d_prev) <= 1) break;
  }
  return invariant;
};

const calc_y = (
  amp: number,
  x_c_amount: number,
  current_c_amounts: number[],
  index_x: number,
  index_y: number
) => {
  const token_num = current_c_amounts.length;
  const ann = amp * token_num ** token_num;
  const invariant = calc_d(amp, current_c_amounts);
  let knownBalanceSum = x_c_amount;
  let newtonConstant = (invariant * invariant) / x_c_amount;
  for (let tokenIndex = 0; tokenIndex < token_num; tokenIndex++) {
    if (tokenIndex != index_x && tokenIndex != index_y) {
      knownBalanceSum += current_c_amounts[tokenIndex];
      newtonConstant =
        (newtonConstant * invariant) / current_c_amounts[tokenIndex];
    }
  }
  newtonConstant =
    (newtonConstant * invariant) / (ann * token_num ** token_num);
  const newtonCoefficient = invariant / ann + knownBalanceSum;
  let y_prev = 0;
  let targetBalance = invariant;
  for (let iteration = 0; iteration < 256; iteration++) {
    y_prev = targetBalance;
    const y_numerator = targetBalance ** 2 + newtonConstant;
    const y_denominator = 2 * targetBalance + newtonCoefficient - invariant;
    targetBalance = y_numerator / y_denominator;
    if (Math.abs(targetBalance - y_prev) <= 1) break;
  }

  return targetBalance;
};

const calc_swap = (
  amp: number,
  in_token_idx: number,
  in_c_amount: number,
  out_token_idx: number,
  old_c_amounts: number[],
  trade_fee: number
) => {
  const newOutputBalance = calc_y(
    amp,
    in_c_amount + old_c_amounts[in_token_idx],
    old_c_amounts,
    in_token_idx,
    out_token_idx
  );
  const outputBeforeFee = old_c_amounts[out_token_idx] - newOutputBalance;
  const fee = tradeFee(outputBeforeFee, trade_fee);
  const amount_swapped = outputBeforeFee - fee;
  return [amount_swapped, fee, outputBeforeFee];
};

export const getSwappedAmount = (
  tokenInId: string,
  tokenOutId: string,
  amountIn: string,
  stablePool: StablePool
) => {
  const amp = stablePool.amp;
  const trade_fee = stablePool.total_fee;

  const STABLE_TOKEN_INDEX = getStableTokenIndex(stablePool.id);

  const in_token_idx = STABLE_TOKEN_INDEX[tokenInId];
  const out_token_idx = STABLE_TOKEN_INDEX[tokenOutId];

  const STABLE_LP_TOKEN_DECIMALS = getStablePoolDecimal(stablePool.id);

  const rates = stablePool.rates.map((rate) =>
    toReadableNumber(STABLE_LP_TOKEN_DECIMALS, rate)
  );

  const base_old_c_amounts = stablePool.c_amounts.map((amount) =>
    toReadableNumber(STABLE_LP_TOKEN_DECIMALS, amount)
  );

  const old_c_amounts = base_old_c_amounts
    .map((amount, index) =>
      toNonDivisibleNumber(
        STABLE_LP_TOKEN_DECIMALS,
        scientificNotationToString(
          new Big(amount || 0).times(new Big(rates[index])).toString()
        )
      )
    )
    .map((amount) => Number(amount));

  const in_c_amount = Number(
    toNonDivisibleNumber(
      STABLE_LP_TOKEN_DECIMALS,
      scientificNotationToString(
        new Big(amountIn).times(new Big(rates[in_token_idx])).toString()
      )
    )
  );

  // const in_c_amount = Number(
  //   toNonDivisibleNumber(STABLE_LP_TOKEN_DECIMALS, amountIn)
  // );

  const [amount_swapped, fee, outputBeforeFee] = calc_swap(
    amp,
    in_token_idx,
    in_c_amount,
    out_token_idx,
    old_c_amounts,
    trade_fee
  );

  // TODO:
  return [
    amount_swapped / Number(rates[out_token_idx]),
    fee,
    outputBeforeFee / Number(rates[out_token_idx]),
  ];

  // return [amount_swapped, fee, dy];
};
